/**
 * Time Analysis Tab Component
 * Performance analysis by hour, day of week, and month
 */

import React, { useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Text,
  useColorModeValue,
  SimpleGrid,
  VStack,
} from "@chakra-ui/react";
import { useTheme } from "next-themes";
import { type ColumnDef } from "@tanstack/react-table";
import {
  Card as UiCard,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { StatisticsCardHeadline, STATS_FONT_FAMILY } from "./statisticsCardHeadline";
import {
  statisticsShadcnBarChartAreaClassName,
  statisticsShadcnCardClassName,
  statisticsShadcnCardContentClassName,
  statisticsShadcnTimeAnalysisChartContainerClassName,
  statisticsTimeAnalysisBarChartMargin,
} from "./statisticsShadcnCardShell";
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import { TradeStatisticsFilters } from "../../../../types/trade";
import { useTimeAnalysis } from "../../../../hooks/useTradeStatistics";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";
import { Skeleton, Alert, AlertIcon } from "@chakra-ui/react";
import { DataTable } from "../../../../components/ui/data-table";
import SignedPnLBarChart from "./StatsSignedPnlBarChart";
import {
  STATISTICS_NO_DATA_TIMEFRAME_MESSAGE,
  StatisticsChartTooltipRow,
} from "./statisticsChartTooltip";
import { StatisticsTabEmptyState, isStatisticsFetchPending, StatisticsTabLoadingState } from "./statisticsEmptyState";
import {
  PNL_NEGATIVE_COLOR,
  PNL_POSITIVE_COLOR,
  toSignedPnLBarChartData,
  type SignedPnLBarDatum,
} from "./statsSignedPnlBarUtils";
import { useDisplayTimeZone } from "../../../../contexts/DisplayTimezoneContext";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import {
  formatYmBucketLongInZone,
  formatYmBucketShortInZone,
  formatUtcHourWallLabel,
} from "../../../../utils/displayDateTime";

type TimeAnalysisChartRow = {
  tradeCount: number;
  winRate: number;
};

type PnLPerformanceRow = {
  totalPnL: number;
};

interface TimeAnalysisTableRow {
  label: string;
  trades: number;
  totalPnL: number;
  winRate: string;
  wins: number;
  losses: number;
}

function makeTimeAnalysisColumns(
  formatMoney: (v: number) => string
): ColumnDef<TimeAnalysisTableRow, unknown>[] {
  return [
    {
      accessorKey: "label",
      header: "Period",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
          {row.getValue("label")}
        </span>
      ),
    },
    {
      accessorKey: "trades",
      header: "Trades",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
          {row.getValue("trades")}
        </span>
      ),
    },
    {
      accessorKey: "totalPnL",
      header: "Total P&L",
      enableSorting: false,
      cell: ({ row }) => {
        const pnl = row.getValue("totalPnL") as number;
        return (
          <span
            data-trade-log-pnl
            className="font-[family-name:var(--font-primary)] text-sm font-medium"
            style={{ color: pnl >= 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR }}
          >
            {formatMoney(pnl)}
          </span>
        );
      },
    },
    {
      accessorKey: "winRate",
      header: "Win Rate",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
          {row.getValue("winRate")}
        </span>
      ),
    },
    {
      accessorKey: "wins",
      header: "Wins",
      enableSorting: false,
      cell: ({ row }) => (
        <span data-trade-log-pnl className="font-[family-name:var(--font-primary)] text-sm font-medium" style={{ color: PNL_POSITIVE_COLOR }}>
          {row.getValue("wins")}
        </span>
      ),
    },
    {
      accessorKey: "losses",
      header: "Losses",
      enableSorting: false,
      cell: ({ row }) => (
        <span data-trade-log-pnl className="font-[family-name:var(--font-primary)] text-sm font-medium" style={{ color: PNL_NEGATIVE_COLOR }}>
          {row.getValue("losses")}
        </span>
      ),
    },
  ];
}

const HOURLY_NO_TIME_DATA_MESSAGE =
  "Hourly performance data requires trade entry times. Please ensure your trades include time information (not just dates) to see performance by hour.";

function TimeAnalysisNoDataCard({
  title,
  message = STATISTICS_NO_DATA_TIMEFRAME_MESSAGE,
  cardBg,
  borderColor,
}: {
  title: string;
  message?: string;
  cardBg: string;
  borderColor: string;
}) {
  return (
    <Box w="100%">
      <Card
        data-statistics-shadcn-card
        bg={cardBg}
        borderRadius="md"
        border="1px solid"
        borderColor={borderColor}
        w="100%"
      >
        <CardBody>
          <StatisticsCardHeadline mb={3}>{title}</StatisticsCardHeadline>
          <Text fontSize="sm" color="gray.500" fontFamily={STATS_FONT_FAMILY}>
            {message}
          </Text>
        </CardBody>
      </Card>
    </Box>
  );
}

function bestWorstPerforming<T extends PnLPerformanceRow>(
  rows: T[],
  getLabel: (row: T) => string
): {
  best: { label: string; pnl: number } | null;
  worst: { label: string; pnl: number } | null;
} {
  if (rows.length === 0) {
    return { best: null, worst: null };
  }

  let bestRow = rows[0];
  let worstRow = rows[0];
  for (const row of rows) {
    if (row.totalPnL > bestRow.totalPnL) {
      bestRow = row;
    }
    if (row.totalPnL < worstRow.totalPnL) {
      worstRow = row;
    }
  }

  return {
    best: { label: getLabel(bestRow), pnl: bestRow.totalPnL },
    worst: { label: getLabel(worstRow), pnl: worstRow.totalPnL },
  };
}

function TimeAnalysisHeaderSummaryStat({
  label,
  valueLabel,
  pnl,
  pnlColor,
  loading,
  withLeadingBorder = false,
  formatMoney,
  valueClassName,
}: {
  label: string;
  valueLabel: string | undefined;
  pnl: number | undefined;
  pnlColor: string;
  loading: boolean;
  withLeadingBorder?: boolean;
  formatMoney: (value: number) => string;
  valueClassName?: string;
}) {
  return (
    <div
      className={cn(
        "relative flex min-w-[140px] flex-1 flex-col justify-center gap-1 border-t border-border px-[22px] py-4 text-left sm:border-t-0",
        withLeadingBorder && "sm:border-l"
      )}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      {loading ? (
        <Skeleton height="28px" width="100px" mt={1} borderRadius="md" />
      ) : (
        <>
          <span
            className={cn(
              "text-lg font-semibold leading-none tabular-nums tracking-tight",
              valueClassName
            )}
            style={{ color: pnlColor }}
          >
            {valueLabel ?? "—"}
          </span>
          {pnl !== undefined ? (
            <span
              className="text-sm font-medium tabular-nums"
              style={{ color: pnlColor }}
            >
              {formatMoney(pnl)}
            </span>
          ) : null}
        </>
      )}
    </div>
  );
}

function TimeAnalysisPerformanceCardHeader<T extends PnLPerformanceRow>({
  title,
  subtitle,
  rows,
  loading,
  formatMoney,
  getLabel,
  bestStatLabel,
  worstStatLabel,
  valueClassName,
}: {
  title: string;
  subtitle?: string;
  rows: T[];
  loading: boolean;
  formatMoney: (value: number) => string;
  getLabel: (row: T) => string;
  bestStatLabel: string;
  worstStatLabel: string;
  valueClassName?: string;
}) {
  const { best, worst } = useMemo(
    () => bestWorstPerforming(rows, getLabel),
    [rows, getLabel]
  );

  return (
    <CardHeader className="!flex w-full flex-col items-stretch gap-0 rounded-none border-b border-border !p-0 sm:flex-row [&.border-b]:!pb-0">
      <div className="flex flex-1 flex-col justify-center gap-1 px-[22px] py-4 sm:py-5">
        <StatisticsCardHeadline>{title}</StatisticsCardHeadline>
        {subtitle ? (
          <CardDescription className="text-xs" style={{ fontFamily: STATS_FONT_FAMILY }}>
            {subtitle}
          </CardDescription>
        ) : null}
      </div>
      <div className="flex w-full sm:w-auto">
        <TimeAnalysisHeaderSummaryStat
          label={bestStatLabel}
          valueLabel={best?.label}
          pnl={best?.pnl}
          pnlColor={PNL_POSITIVE_COLOR}
          loading={loading}
          formatMoney={formatMoney}
          valueClassName={valueClassName}
        />
        <TimeAnalysisHeaderSummaryStat
          label={worstStatLabel}
          valueLabel={worst?.label}
          pnl={worst?.pnl}
          pnlColor={PNL_NEGATIVE_COLOR}
          loading={loading}
          withLeadingBorder
          formatMoney={formatMoney}
          valueClassName={valueClassName}
        />
      </div>
    </CardHeader>
  );
}

const timeAnalysisSignedPnlChartProps = {
  className: statisticsShadcnTimeAnalysisChartContainerClassName,
  chartMargin: statisticsTimeAnalysisBarChartMargin,
} as const;

function TimeAnalysisChartCard({
  themeClass,
  header,
  children,
  footer,
}: {
  themeClass: string;
  header: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <UiCard
      data-statistics-shadcn-card
      className={cn(
        statisticsShadcnCardClassName,
        themeClass
      )}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      {header}
      <CardContent className={cn(statisticsShadcnCardContentClassName, "pt-4")}>
        <div className={statisticsShadcnBarChartAreaClassName}>{children}</div>
      </CardContent>
      {footer && <div className="px-4 pb-4">{footer}</div>}
    </UiCard>
  );
}

function timeAnalysisTooltipExtra(
  row: SignedPnLBarDatum & Partial<TimeAnalysisChartRow>,
  formatPercent: (value: number) => string
) {
  return (
    <>
      <StatisticsChartTooltipRow label="Trades" value={row.tradeCount ?? 0} />
      <StatisticsChartTooltipRow label="Win Rate" value={formatPercent(row.winRate ?? 0)} />
    </>
  );
}

interface TimeAnalysisTabProps {
  filters?: TradeStatisticsFilters;
  /** When provided, cards show Selected period and Comparison period versions */
  comparisonFilters?: TradeStatisticsFilters;
  /** When false (selected and comparison in same month), Performance by Month shows only the normal card */
  showMonthlyComparison?: boolean;
}

const TimeAnalysisTab: React.FC<TimeAnalysisTabProps> = ({ filters, comparisonFilters, showMonthlyComparison = true }) => {
  const { resolvedTheme } = useTheme();
  const { formatMoney } = useDisplayCurrency();
  const displayTimeZone = useDisplayTimeZone();
  const themeClass = resolvedTheme === "dark" ? "dark" : "";
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // Fetch time analysis from backend
  const { data, loading, error, errorAction } = useTimeAnalysis(filters);
  const { data: comparisonData, loading: comparisonLoading, error: comparisonError, errorAction: comparisonErrorAction } =
    useTimeAnalysis(comparisonFilters, { enabled: Boolean(comparisonFilters) });
  const hasComparisonFilters = Boolean(
    comparisonFilters?.start_date && comparisonFilters?.end_date
  );
  const widgetGateError = error || (hasComparisonFilters ? comparisonError : null);
  const widgetGateAction =
    error != null
      ? errorAction
      : hasComparisonFilters && comparisonError != null
        ? comparisonErrorAction
        : null;
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(
    widgetGateError,
    widgetGateAction
  );

  // Transform backend data to component format (all hours from API or empty)
  const hourlyPerformance = useMemo(() => {
    if (!data || !data.by_hour) {
      return [];
    }
    return data.by_hour.map((h) => ({
      hour: h.hour,
      totalPnL: h.total_pnl,
      tradeCount: h.trade_count,
      winningTrades: h.winning_trades,
      losingTrades: h.losing_trades,
      winRate: h.win_rate,
    }));
  }, [data]);

  // Only hours that have data (tradeCount > 0) — chart and table show these only
  const hourlyWithData = useMemo(
    () => hourlyPerformance.filter((h) => h.tradeCount > 0),
    [hourlyPerformance]
  );

  const dayOfWeekPerformance = useMemo(() => {
    if (!data || !data.by_day_of_week) {
      return [];
    }
    // Map backend day_index (Monday=0) to day name
    const dayMap: { [key: number]: string } = {
      0: "Monday",
      1: "Tuesday",
      2: "Wednesday",
      3: "Thursday",
      4: "Friday",
      5: "Saturday",
      6: "Sunday",
    };
    return data.by_day_of_week.map((d) => ({
      day: dayMap[d.day_index] || d.day,
      totalPnL: d.total_pnl,
      tradeCount: d.trade_count,
      winningTrades: d.winning_trades,
      losingTrades: d.losing_trades,
      winRate: d.win_rate,
    }));
  }, [data]);

  // Only days that have data (tradeCount > 0), sorted by day_index (Mon=0 … Sun=6) so order is consistent
  const dayOfWeekWithData = useMemo(() => {
    const withData = dayOfWeekPerformance.filter((d) => d.tradeCount > 0);
    const dayOrder: Record<string, number> = {
      Monday: 0,
      Tuesday: 1,
      Wednesday: 2,
      Thursday: 3,
      Friday: 4,
      Saturday: 5,
      Sunday: 6,
    };
    return [...withData].sort((a, b) => (dayOrder[a.day] ?? 0) - (dayOrder[b.day] ?? 0));
  }, [dayOfWeekPerformance]);

  const monthlyPerformance = useMemo(() => {
    if (!data || !data.by_month) {
      return [];
    }
    return data.by_month
      .map((m) => {
        const monthKey = String(m.month).slice(0, 7);
        const monthName = formatYmBucketLongInZone(monthKey, displayTimeZone);
        return {
          monthKey,
          month: monthName,
          totalPnL: m.total_pnl,
          tradeCount: m.trade_count,
          winningTrades: m.winning_trades,
          losingTrades: m.losing_trades,
          winRate: m.win_rate,
        };
      })
      .sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  }, [data, displayTimeZone]);

  // Only months that have data (tradeCount > 0) — chart and table show these only
  const monthlyWithData = useMemo(
    () => monthlyPerformance.filter((m) => m.tradeCount > 0),
    [monthlyPerformance]
  );

  // Comparison period data (same shape as above, from comparisonData)
  const comparisonHourlyPerformance = useMemo(() => {
    if (!comparisonData || !comparisonData.by_hour) return [];
    return comparisonData.by_hour.map((h) => ({
      hour: h.hour,
      totalPnL: h.total_pnl,
      tradeCount: h.trade_count,
      winningTrades: h.winning_trades,
      losingTrades: h.losing_trades,
      winRate: h.win_rate,
    }));
  }, [comparisonData]);
  const comparisonHourlyWithData = useMemo(
    () => comparisonHourlyPerformance.filter((h) => h.tradeCount > 0),
    [comparisonHourlyPerformance]
  );

  const comparisonDayOfWeekPerformance = useMemo(() => {
    if (!comparisonData || !comparisonData.by_day_of_week) return [];
    const dayMap: { [key: number]: string } = {
      0: "Monday", 1: "Tuesday", 2: "Wednesday", 3: "Thursday",
      4: "Friday", 5: "Saturday", 6: "Sunday",
    };
    return comparisonData.by_day_of_week.map((d) => ({
      day: dayMap[d.day_index] || d.day,
      totalPnL: d.total_pnl,
      tradeCount: d.trade_count,
      winningTrades: d.winning_trades,
      losingTrades: d.losing_trades,
      winRate: d.win_rate,
    }));
  }, [comparisonData]);
  const comparisonDayOfWeekWithData = useMemo(() => {
    const withData = comparisonDayOfWeekPerformance.filter((d) => d.tradeCount > 0);
    const dayOrder: Record<string, number> = {
      Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3,
      Friday: 4, Saturday: 5, Sunday: 6,
    };
    return [...withData].sort((a, b) => (dayOrder[a.day] ?? 0) - (dayOrder[b.day] ?? 0));
  }, [comparisonDayOfWeekPerformance]);

  const comparisonMonthlyPerformance = useMemo(() => {
    if (!comparisonData || !comparisonData.by_month) return [];
    return comparisonData.by_month
      .map((m) => {
        const monthKey = String(m.month).slice(0, 7);
        const monthName = formatYmBucketLongInZone(monthKey, displayTimeZone);
        return {
          monthKey,
          month: monthName,
          totalPnL: m.total_pnl,
          tradeCount: m.trade_count,
          winningTrades: m.winning_trades,
          losingTrades: m.losing_trades,
          winRate: m.win_rate,
        };
      })
      .sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  }, [comparisonData, displayTimeZone]);
  const comparisonMonthlyWithData = useMemo(
    () => comparisonMonthlyPerformance.filter((m) => m.tradeCount > 0),
    [comparisonMonthlyPerformance]
  );

  // Format percentage
  const formatPercent = (value: number): string => {
    return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
  };

  const hourlyChartData = useMemo(
    () =>
      toSignedPnLBarChartData(hourlyWithData, (h) =>
        formatUtcHourWallLabel(h.hour, displayTimeZone)
      ),
    [hourlyWithData, displayTimeZone]
  );

  const dayOfWeekChartData = useMemo(
    () => toSignedPnLBarChartData(dayOfWeekWithData, (d) => d.day),
    [dayOfWeekWithData]
  );

  const monthlyChartData = useMemo(
    () => toSignedPnLBarChartData(monthlyWithData, (m) => m.month),
    [monthlyWithData]
  );

  const comparisonHourlyChartData = useMemo(
    () =>
      toSignedPnLBarChartData(comparisonHourlyWithData, (h) =>
        formatUtcHourWallLabel(h.hour, displayTimeZone)
      ),
    [comparisonHourlyWithData, displayTimeZone]
  );

  const comparisonDayOfWeekChartData = useMemo(
    () => toSignedPnLBarChartData(comparisonDayOfWeekWithData, (d) => d.day),
    [comparisonDayOfWeekWithData]
  );

  const comparisonMonthlyChartData = useMemo(
    () => toSignedPnLBarChartData(comparisonMonthlyWithData, (m) => m.month),
    [comparisonMonthlyWithData]
  );

  const getHourLabel = useCallback(
    (row: { hour: number; totalPnL: number }) =>
      formatUtcHourWallLabel(row.hour, displayTimeZone),
    [displayTimeZone]
  );

  const getDayLabel = useCallback((row: { day: string; totalPnL: number }) => row.day, []);

  const getMonthLabel = useCallback((row: { month: string; totalPnL: number }) => row.month, []);

  const getMonthHeaderLabel = useCallback(
    (row: { monthKey: string; totalPnL: number }) =>
      formatYmBucketShortInZone(row.monthKey, displayTimeZone),
    [displayTimeZone]
  );

  const hasComparison = Boolean(
    comparisonFilters?.start_date &&
    comparisonFilters?.end_date &&
    comparisonData
  );

  const selectedAllTimeBlocksEmpty =
    hourlyWithData.length === 0 &&
    dayOfWeekWithData.length === 0 &&
    monthlyWithData.length === 0;

  const comparisonAllTimeBlocksEmpty =
    comparisonHourlyWithData.length === 0 &&
    comparisonDayOfWeekWithData.length === 0 &&
    comparisonMonthlyWithData.length === 0;

  /** Single centered empty when hour / day / month all have no trades (both periods in compare mode). */
  const showUnifiedTimeAnalysisEmpty =
    !error &&
    !loading &&
    (!hasComparison || !comparisonLoading) &&
    selectedAllTimeBlocksEmpty &&
    (!hasComparison || comparisonAllTimeBlocksEmpty);

  const hourlyBlock = (
    <>
      {/* Performance by Hour */}
      {hourlyWithData.length > 0 ? (
        <>
          {/* Chart and Table Card - Connected */}
          <Box w="100%">
            <TimeAnalysisChartCard
              themeClass={themeClass}
              header={
                <TimeAnalysisPerformanceCardHeader
                  title="Performance by Hour of Day"
                  rows={hourlyWithData}
                  loading={loading}
                  formatMoney={formatMoney}
                  getLabel={getHourLabel}
                  bestStatLabel="Best Hour"
                  worstStatLabel="Worst Hour"
                />
              }
              footer={
                <DataTable
                  columns={makeTimeAnalysisColumns(formatMoney)}
                  data={hourlyWithData.map((hour) => ({
                    label: formatUtcHourWallLabel(hour.hour, displayTimeZone),
                    trades: hour.tradeCount,
                    totalPnL: hour.totalPnL,
                    winRate: formatPercent(hour.winRate),
                    wins: hour.winningTrades,
                    losses: hour.losingTrades,
                  }))}
                  showPagination={false}
                  emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
                />
              }
            >
              <SignedPnLBarChart
                {...timeAnalysisSignedPnlChartProps}
                data={hourlyChartData}
                symmetricYAxis
                renderTooltipExtra={(row) => timeAnalysisTooltipExtra(row, formatPercent)}
              />
            </TimeAnalysisChartCard>
          </Box>
        </>
      ) : (
        <TimeAnalysisNoDataCard
          title="Performance by Hour of Day"
          message={HOURLY_NO_TIME_DATA_MESSAGE}
          cardBg={cardBg}
          borderColor={borderColor}
        />
      )}
    </>
  );

  const dayOfWeekBlock =
    dayOfWeekWithData.length > 0 ? (
      <Box w="100%">
        <TimeAnalysisChartCard
          themeClass={themeClass}
          header={
            <TimeAnalysisPerformanceCardHeader
              title="Performance by Day of Week"
              rows={dayOfWeekWithData}
              loading={loading}
              formatMoney={formatMoney}
              getLabel={getDayLabel}
              bestStatLabel="Best Day"
              worstStatLabel="Worst Day"
            />
          }
          footer={
            <DataTable
              columns={makeTimeAnalysisColumns(formatMoney)}
              data={dayOfWeekPerformance.map((day) => ({
                label: day.day,
                trades: day.tradeCount,
                totalPnL: day.totalPnL,
                winRate: formatPercent(day.winRate),
                wins: day.winningTrades,
                losses: day.losingTrades,
              }))}
              showPagination={false}
              emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
            />
          }
        >
          <SignedPnLBarChart
            {...timeAnalysisSignedPnlChartProps}
            data={dayOfWeekChartData}
            symmetricYAxis
            renderTooltipExtra={(row) => timeAnalysisTooltipExtra(row, formatPercent)}
          />
        </TimeAnalysisChartCard>
      </Box>
    ) : (
      <TimeAnalysisNoDataCard
        title="Performance by Day of Week"
        cardBg={cardBg}
        borderColor={borderColor}
      />
    );

  const monthBlock =
    monthlyWithData.length > 0 ? (
      <Box w="100%">
        <TimeAnalysisChartCard
          themeClass={themeClass}
          header={
            <TimeAnalysisPerformanceCardHeader
              title="Performance by Month"
              rows={monthlyWithData}
              loading={loading}
              formatMoney={formatMoney}
              getLabel={getMonthHeaderLabel}
              bestStatLabel="Best Month"
              worstStatLabel="Worst Month"
              valueClassName="whitespace-nowrap"
            />
          }
          footer={
            <DataTable
              columns={makeTimeAnalysisColumns(formatMoney)}
              data={monthlyPerformance.map((month) => ({
                label: month.month,
                trades: month.tradeCount,
                totalPnL: month.totalPnL,
                winRate: formatPercent(month.winRate),
                wins: month.winningTrades,
                losses: month.losingTrades,
              }))}
              showPagination={false}
              emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
            />
          }
        >
          <SignedPnLBarChart
            {...timeAnalysisSignedPnlChartProps}
            data={monthlyChartData}
            symmetricYAxis
            renderTooltipExtra={(row) => timeAnalysisTooltipExtra(row, formatPercent)}
          />
        </TimeAnalysisChartCard>
      </Box>
    ) : (
      <TimeAnalysisNoDataCard
        title="Performance by Month"
        cardBg={cardBg}
        borderColor={borderColor}
      />
    );

  const comparisonHourlyBlock = !hasComparison ? null : comparisonHourlyWithData.length > 0 ? (
        <Box w="100%">
          <TimeAnalysisChartCard
            themeClass={themeClass}
            header={
              <TimeAnalysisPerformanceCardHeader
                title="Performance by Hour of Day"
                rows={comparisonHourlyWithData}
                loading={comparisonLoading}
                formatMoney={formatMoney}
                getLabel={getHourLabel}
                bestStatLabel="Best Hour"
                worstStatLabel="Worst Hour"
              />
            }
            footer={
              <DataTable
                columns={makeTimeAnalysisColumns(formatMoney)}
                data={comparisonHourlyWithData.map((hour) => ({
                  label: formatUtcHourWallLabel(hour.hour, displayTimeZone),
                  trades: hour.tradeCount,
                  totalPnL: hour.totalPnL,
                  winRate: formatPercent(hour.winRate),
                  wins: hour.winningTrades,
                  losses: hour.losingTrades,
                }))}
                showPagination={false}
                emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
              />
            }
          >
            <SignedPnLBarChart
              {...timeAnalysisSignedPnlChartProps}
              data={comparisonHourlyChartData}
              symmetricYAxis
              renderTooltipExtra={(row) => timeAnalysisTooltipExtra(row, formatPercent)}
            />
          </TimeAnalysisChartCard>
        </Box>
      ) : (
        <TimeAnalysisNoDataCard
          title="Performance by Hour of Day"
          message={HOURLY_NO_TIME_DATA_MESSAGE}
          cardBg={cardBg}
          borderColor={borderColor}
        />
      );

  const comparisonDayBlock = !hasComparison ? null : comparisonDayOfWeekWithData.length > 0 ? (
        <Box w="100%">
          <TimeAnalysisChartCard
            themeClass={themeClass}
            header={
              <TimeAnalysisPerformanceCardHeader
                title="Performance by Day of Week"
                rows={comparisonDayOfWeekWithData}
                loading={comparisonLoading}
                formatMoney={formatMoney}
                getLabel={getDayLabel}
                bestStatLabel="Best Day"
                worstStatLabel="Worst Day"
              />
            }
            footer={
              <DataTable
                columns={makeTimeAnalysisColumns(formatMoney)}
                data={comparisonDayOfWeekWithData.map((day) => ({
                  label: day.day,
                  trades: day.tradeCount,
                  totalPnL: day.totalPnL,
                  winRate: formatPercent(day.winRate),
                  wins: day.winningTrades,
                  losses: day.losingTrades,
                }))}
                showPagination={false}
                emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
              />
            }
          >
            <SignedPnLBarChart
              {...timeAnalysisSignedPnlChartProps}
              data={comparisonDayOfWeekChartData}
              symmetricYAxis
              renderTooltipExtra={(row) => timeAnalysisTooltipExtra(row, formatPercent)}
            />
          </TimeAnalysisChartCard>
        </Box>
      ) : (
        <TimeAnalysisNoDataCard
          title="Performance by Day of Week"
          cardBg={cardBg}
          borderColor={borderColor}
        />
      );

  const comparisonMonthBlock = !hasComparison ? null : comparisonMonthlyWithData.length > 0 ? (
        <Box w="100%">
          <TimeAnalysisChartCard
            themeClass={themeClass}
            header={
              <TimeAnalysisPerformanceCardHeader
                title="Performance by Month"
                rows={comparisonMonthlyWithData}
                loading={comparisonLoading}
                formatMoney={formatMoney}
                getLabel={getMonthHeaderLabel}
                bestStatLabel="Best Month"
                worstStatLabel="Worst Month"
                valueClassName="whitespace-nowrap"
              />
            }
            footer={
              <DataTable
                columns={makeTimeAnalysisColumns(formatMoney)}
                data={comparisonMonthlyPerformance.map((month) => ({
                  label: month.month,
                  trades: month.tradeCount,
                  totalPnL: month.totalPnL,
                  winRate: formatPercent(month.winRate),
                  wins: month.winningTrades,
                  losses: month.losingTrades,
                }))}
                showPagination={false}
                emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
              />
            }
          >
            <SignedPnLBarChart
              {...timeAnalysisSignedPnlChartProps}
              data={comparisonMonthlyChartData}
              symmetricYAxis
              renderTooltipExtra={(row) => timeAnalysisTooltipExtra(row, formatPercent)}
            />
          </TimeAnalysisChartCard>
        </Box>
      ) : (
        <TimeAnalysisNoDataCard
          title="Performance by Month"
          cardBg={cardBg}
          borderColor={borderColor}
        />
      );

  if (isStatisticsFetchPending(data, error)) {
    return <StatisticsTabLoadingState />;
  }

  if (hasComparisonFilters && isStatisticsFetchPending(comparisonData, comparisonError)) {
    return <StatisticsTabLoadingState />;
  }

  if (showWidgetError && widgetError) {
    return <StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} />;
  }

  if (showUnifiedTimeAnalysisEmpty) {
    return <StatisticsTabEmptyState />;
  }

  return (
    // @ts-ignore - Chakra UI complex union type
    <VStack spacing="24px" align="stretch">
    {hasComparison ? (
      <>
        {/* Performance by Hour: Selected then Comparison */}
        <Box data-statistics-card-surface="" w="100%" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={4} bg={cardBg}>
          {hourlyBlock}
        </Box>
        <Box data-statistics-card-surface="" w="100%" border="2px solid" borderColor="#F49113" borderRadius="lg" p={4} bg={cardBg}>
          {comparisonHourlyBlock}
        </Box>
        {/* Performance by Day of Week: Selected then Comparison */}
        <Box data-statistics-card-surface="" w="100%" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={4} bg={cardBg}>
          {dayOfWeekBlock}
        </Box>
        <Box data-statistics-card-surface="" w="100%" border="2px solid" borderColor="#F49113" borderRadius="lg" p={4} bg={cardBg}>
          {comparisonDayBlock}
        </Box>
        {/* Performance by Month: Selected then Comparison when different months; otherwise single card */}
        {showMonthlyComparison ? (
          <>
            <Box data-statistics-card-surface="" w="100%" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={4} bg={cardBg}>
              {monthBlock}
            </Box>
            <Box data-statistics-card-surface="" w="100%" border="2px solid" borderColor="#F49113" borderRadius="lg" p={4} bg={cardBg}>
              {comparisonMonthBlock}
            </Box>
          </>
        ) : (
          <Box w="100%">{monthBlock}</Box>
        )}
      </>
    ) : (
      <>
        {hourlyBlock}
        {dayOfWeekBlock}
        {monthBlock}
      </>
    )}
    </VStack>
  );
};

export default TimeAnalysisTab;

